import { NotificationEvent, NotificationSystem, useNotifications } from '@swordhealth/ui'

import { EVENT_KEYS } from '@/utils/segment/segment-events'
import { UI_BLOCKERS } from '@/utils/segment/schemas/ui-blocker'
import { segment } from '@/utils/segment'
import { TOASTER_KEY } from '@/scripts/app-configs/constants'

type ErrorOptions = {
  trackError?: boolean,
  segmentPayload?: {
    error?: unknown,
    programUuid?: string | number | null,
  },
}

/**
 * Wraps UI notifications to use the app toaster key and optionally track errors in Segment.
 *
 * @example
 * const notify = useAppNotifications()
 *
 * notify.success('Changes saved successfully')
 *
 * notify.error('Failed to save changes', {
 *   trackError: true,
 *   segmentPayload: {
 *     error,
 *     programUuid,
 *   },
 * })
 */

interface AppNotificationSystem extends NotificationSystem {
  error: (notification: NotificationEvent | string, options?: ErrorOptions) => void
}

export const useAppNotifications = (key: symbol | string = TOASTER_KEY): AppNotificationSystem => {
  const notifications = useNotifications(key)

  if (!notifications) {
    console.warn('Notifications are not available.')

    return new Proxy({}, {
      get: () => () => {},
    }) as NotificationSystem
  }

  const error = (notification: NotificationEvent | string, options: ErrorOptions = {
    trackError: false,
    segmentPayload: {
      error: null,
      programUuid: null,
    },
  }) => {
    const { trackError, segmentPayload } = options

    notifications.error(notification)

    if (!trackError) return

    segment.trackEventWithUser(EVENT_KEYS.UI_BLOCKER, {
      blocker: UI_BLOCKERS.ERROR_ALERT,
      request: (segmentPayload?.error as {config: {url: string}})?.config?.url,
      programUuid: segmentPayload?.programUuid?.toString(),
    })
  }

  return {
    ...notifications,
    error,
  }
}

export default useAppNotifications
